import React from 'react'
import { useIntl } from 'react-intl'
import ExperienceLuxPlusCarousel from 'components/Experiences/ExperienceLists/ExperienceLuxPlusCarousel'
import OfferCardCarouselSection from 'components/Common/OfferCardCarousel/OfferCardCarouselSection'
import { OfferListEventsProvider } from 'components/OfferList/OfferListEventsContext'
import useOfferListTracking from 'hooks/Offers/useOfferListTracking'
import { getListName } from 'analytics/enhanced-ecommerce'
import Places from 'constants/places'
import { getSearchQueryFromFilter } from 'lib/search/searchUtils'
import { useAppSelector } from 'hooks/reduxHooks'
import { getIsLuxPlusToursEnabled } from 'checkout/selectors/tourV2Selectors'
import { getIsEnabledLuxPlusCruiseInclusions } from 'luxPlus/selectors/featureToggle'
import LuxPlusFilteredOfferCarousel, { type OfferCategory } from './LuxPlusFilteredOfferCarousel'
import productLabels from 'i18n/productLabels'

const ANALYTICS_PAGE = 'LUXPLUS-LANDING'

const HOTEL_OFFER_TYPES: App.OfferListFilters['offerTypes'] = ['hotel', 'tactical_ao_hotel']
const TOUR_OFFER_TYPES: App.OfferListFilters['offerTypes'] = ['tour_v2']

const HOTEL_SORT_BY: App.OfferListSortByOptions = 'luxPlusList'
const TOUR_CATEGORIES: Array<OfferCategory> = ['member-discounts']
const TOUR_SORT_BY: App.OfferListSortByOptions = 'homepage'

const CRUISE_FILTERS: App.OfferListFilters = {
  offerTypes: ['cruise'],
  destinations: [Places.Anywhere.id],
  sortBy: 'luxPlusList',
  hasLuxPlusPrice: true,
  limit: 20,
}

const CRUISE_ALL_LINK = `/search/cruises?${getSearchQueryFromFilter(CRUISE_FILTERS)}`

function LuxPlusLandingOfferCarousels() {
  const intl = useIntl()
  const isLuxPlusToursEnabled = useAppSelector(getIsLuxPlusToursEnabled)
  const isEnabledLuxPlusCruiseInclusions = useAppSelector(getIsEnabledLuxPlusCruiseInclusions)

  const cruiseTracking = useOfferListTracking(getListName('OfferList', ANALYTICS_PAGE, 'Cruise bonus inclusions'))

  const localExperiencesTitle = intl.formatMessage({
    id: 'rvMgQO_VW7Vv0ww-Q16g',
    defaultMessage: 'Local experiences',
    description: 'Title for local experiences offer carousel on LuxPlus landing page',
  })
  const hotelsTitle = intl.formatMessage(productLabels.hotels)
  const toursTitle = intl.formatMessage(productLabels.tours)
  const cruisesTitle = intl.formatMessage({
    id: 'gRPQiITTUNWPFLbWZKot',
    defaultMessage: 'Cruises',
    description: 'Title for cruises offer carousel on LuxPlus landing page',
  })

  return (<>
    <ExperienceLuxPlusCarousel title={localExperiencesTitle} />
    <LuxPlusFilteredOfferCarousel
      title={hotelsTitle}
      offerTypes={HOTEL_OFFER_TYPES}
      sortBy={HOTEL_SORT_BY}
      analyticsPage={ANALYTICS_PAGE}
      allLinkBase="/search"
      carouselIdPrefix="discounted_flash"
    />
    {isLuxPlusToursEnabled && <LuxPlusFilteredOfferCarousel
      title={toursTitle}
      offerTypes={TOUR_OFFER_TYPES}
      sortBy={TOUR_SORT_BY}
      categories={TOUR_CATEGORIES}
      analyticsPage={ANALYTICS_PAGE}
      allLinkBase="/search/tours"
      carouselIdPrefix="discounted_tours"
    />}
    {isEnabledLuxPlusCruiseInclusions && <OfferListEventsProvider tracking={cruiseTracking}>
      <OfferCardCarouselSection
        tileSize="medium"
        title={cruisesTitle}
        filters={CRUISE_FILTERS}
        allLink={CRUISE_ALL_LINK}
        minimumOffersCount={4}
        id="cruise_inclusions"
      />
    </OfferListEventsProvider>}
  </>)
}

export default LuxPlusLandingOfferCarousels
